import { z } from 'zod';
import { listQuerySchema } from '../common/pagination';
import { moneySchema } from '../money';

/**
 * Tatsächliche Reisekosten (Barbelege) zu einer Reise — ersetzt die bisherige
 * Excel-Liste. Mitarbeiter erfassen Positionen (Datum, Reisezweck, vier
 * Kostenarten wie in der Vorlage), reichen sie ein; die Buchhaltung
 * (`accounting.approve`) prüft und gibt frei — bei Freigabe entsteht EINE
 * Sammelbuchung im Kassabuch (Rückmeldung 2026-08-25).
 */

export const tripExpenseStatusSchema = z.enum(['PENDING', 'APPROVED', 'REJECTED']);
export type TripExpenseStatus = z.infer<typeof tripExpenseStatusSchema>;

export const TRIP_EXPENSE_STATUS_LABELS: Record<TripExpenseStatus, string> = {
  PENDING: 'Ausstehend',
  APPROVED: 'Freigegeben',
  REJECTED: 'Abgelehnt',
};

/** Betrag einer Kostenart: EUR, nie negativ (0 = leeres Feld in der Vorlage). */
const tripExpenseAmountSchema = moneySchema.refine(
  (value) => value.currency === 'EUR' && BigInt(value.amount) >= 0n,
  'Betrag muss 0 oder positiv und in EUR sein.',
);

/** Eine Zeile der Vorlage: Datum, Reisezweck, Fahrt-/Hotel-/Verpflegungs-/Sonstige Kosten. */
export const tripExpenseItemInputSchema = z
  .object({
    date: z.string().date(),
    purpose: z.string().trim().min(1).max(200),
    travelAmount: tripExpenseAmountSchema,
    hotelAmount: tripExpenseAmountSchema,
    mealsAmount: tripExpenseAmountSchema,
    otherAmount: tripExpenseAmountSchema,
  })
  .refine(
    (item) =>
      BigInt(item.travelAmount.amount) +
        BigInt(item.hotelAmount.amount) +
        BigInt(item.mealsAmount.amount) +
        BigInt(item.otherAmount.amount) >
      0n,
    { message: 'Mindestens ein Kostenbetrag muss größer als 0 sein.', path: ['travelAmount'] },
  );
export type TripExpenseItemInput = z.infer<typeof tripExpenseItemInputSchema>;

export const createTripExpenseReportSchema = z.object({
  tripId: z.string().uuid(),
  items: z.array(tripExpenseItemInputSchema).min(1, 'Mindestens eine Position erforderlich.'),
});
export type CreateTripExpenseReportInput = z.infer<typeof createTripExpenseReportSchema>;

/** Bearbeiten (PENDING) bzw. korrigiert erneut einreichen (REJECTED) — ersetzt alle Positionen. */
export const updateTripExpenseReportSchema = z.object({
  items: z.array(tripExpenseItemInputSchema).min(1, 'Mindestens eine Position erforderlich.'),
});
export type UpdateTripExpenseReportInput = z.infer<typeof updateTripExpenseReportSchema>;

export const tripExpenseItemResponseSchema = z.object({
  id: z.string().uuid(),
  date: z.string().datetime(),
  purpose: z.string(),
  travelAmount: moneySchema,
  hotelAmount: moneySchema,
  mealsAmount: moneySchema,
  otherAmount: moneySchema,
  /** Summe der vier Kostenarten dieser Zeile (= „Betrag" in der Vorlage). */
  amount: moneySchema,
});
export type TripExpenseItemResponse = z.infer<typeof tripExpenseItemResponseSchema>;

export const tripExpenseReportResponseSchema = z.object({
  id: z.string().uuid(),
  tripId: z.string().uuid(),
  tripTitle: z.string(),
  employeeId: z.string().uuid(),
  employeeName: z.string(),
  status: tripExpenseStatusSchema,
  items: z.array(tripExpenseItemResponseSchema),
  /** Summe aller Positionen (= „Summe Gesamt" in der Vorlage). */
  totalAmount: moneySchema,
  reviewerName: z.string().nullable(),
  reviewedAt: z.string().datetime().nullable(),
  adminNote: z.string().nullable(),
  /** Gesetzt, sobald die Sammelbuchung im Kassabuch angelegt wurde. */
  postedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type TripExpenseReportResponse = z.infer<typeof tripExpenseReportResponseSchema>;

/** Freigeben/Ablehnen mit optionaler Notiz (Buchhaltung). */
export const reviewTripExpenseReportSchema = z.object({
  adminNote: z.string().trim().max(2000).optional(),
});
export type ReviewTripExpenseReportInput = z.infer<typeof reviewTripExpenseReportSchema>;

/** Listen-Query der Buchhaltungs-Prüfliste: Pagination + Filter Status/Mitarbeiter. */
export const tripExpenseReportsQuerySchema = listQuerySchema.extend({
  status: tripExpenseStatusSchema.optional(),
  employeeId: z.string().uuid().optional(),
});
export type TripExpenseReportsQuery = z.infer<typeof tripExpenseReportsQuerySchema>;
